// Profil — wer bin ich, und wie ändere ich mein Passwort.
//
// Jeder kann das für sich selbst, ohne den Chef zu fragen. Wichtig, weil der
// Chef die Startpasswörter vergibt und jeder sie danach ersetzen sollte.

import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/stores/authStore";
import { ROLLEN } from "@/lib/team";
import { useMeinProfil, useAvatarHochladen, useAvatarEntfernen, useMailAendern } from "@/lib/profil";
import { useTheme } from "@/lib/theme";
import { Button, Card, Field, Input, Segmented, cn } from "@/design-system/ui";
import { ModulHeader } from "@/modules/home/ModulHeader";

export default function ProfilPage() {
  const navigate = useNavigate();
  const session = useAuth((s) => s.session);
  const role = useAuth((s) => s.role);
  const signOut = useAuth((s) => s.signOut);

  const { data: profil } = useMeinProfil();

  const email = session?.user.email ?? "";
  // Der Name kommt bevorzugt aus dem Profil — dort wird er gepflegt. Die Sitzung
  // trägt ihn nur als Kopie und hinkt nach einer Änderung hinterher.
  const name =
    profil?.full_name ??
    (session?.user.user_metadata as { full_name?: string } | undefined)?.full_name ??
    "";

  const hochladen = useAvatarHochladen();
  const entfernen = useAvatarEntfernen();
  const mailAendern = useMailAendern();
  const { mode, setTheme } = useTheme();
  const bildRef = useRef<HTMLInputElement>(null);
  const [bildFehler, setBildFehler] = useState<string | null>(null);
  const [neueMail, setNeueMail] = useState("");
  const [mailHinweis, setMailHinweis] = useState<{ ok: boolean; text: string } | null>(null);

  const [alt, setAlt] = useState("");
  const [neu, setNeu] = useState("");
  const [wdh, setWdh] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState(false);

  const zuKurz = neu.length > 0 && neu.length < 8;
  const ungleich = wdh.length > 0 && neu !== wdh;
  const kannSpeichern = alt.length >= 1 && neu.length >= 8 && neu === wdh;

  const aendern = async () => {
    setFehler(null);
    setErfolg(false);
    setLaeuft(true);
    try {
      // Erst das ALTE Passwort prüfen. Supabase würde die Änderung auch ohne
      // erlauben — dann könnte jeder, der ein offenes Gerät findet, das Konto
      // übernehmen.
      const { error: authErr } = await supabase.auth.signInWithPassword({
        email,
        password: alt,
      });
      if (authErr) {
        setFehler("Das bisherige Passwort stimmt nicht.");
        return;
      }

      const { error } = await supabase.auth.updateUser({ password: neu });
      if (error) {
        setFehler(error.message);
        return;
      }

      // Startpasswort ist ersetzt — der Hinweis darf verschwinden.
      if (session?.user.id) {
        await supabase
          .from("profiles")
          .update({ must_change_password: false })
          .eq("id", session.user.id);
      }

      setErfolg(true);
      setAlt("");
      setNeu("");
      setWdh("");
    } catch (e) {
      setFehler((e as Error).message);
    } finally {
      setLaeuft(false);
    }
  };

  const rolleLabel = ROLLEN.find((r) => r.wert === role)?.label ?? role;

  return (
    <div className="mx-auto flex h-full max-w-xl flex-col">
      <ModulHeader titel="Mein Profil" />

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-5">
        {/* Wer bin ich — mit Bild zum Antippen */}
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => bildRef.current?.click()}
              disabled={hochladen.isPending}
              aria-label={profil?.avatar_url ? "Profilbild ändern" : "Profilbild hochladen"}
              className={cn(
                "group relative grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-full",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60",
                profil?.avatar_url
                  ? "ring-1 ring-ink-600"
                  : "bg-gruen-500/15 text-xl font-semibold text-gruen-400"
              )}
            >
              {profil?.avatar_url ? (
                <img src={profil.avatar_url} alt="" className="h-full w-full object-cover" />
              ) : (
                (name || email).slice(0, 1).toUpperCase()
              )}
              {/* Der Hinweis liegt AUF dem Bild statt daneben: Ein rundes Bild
                  mit Kamera-Zeichen erklärt sich, eine Beschriftung daneben
                  bräuchte Platz, den die Zeile nicht hat. */}
              <span className="absolute inset-x-0 bottom-0 bg-black/55 py-0.5 text-center text-[10px] font-medium text-white">
                {hochladen.isPending ? "…" : "ändern"}
              </span>
            </button>

            <div className="min-w-0 flex-1">
              <div className="truncate text-[15px] font-semibold text-ink-50">
                {name || "Ohne Namen"}
              </div>
              <div className="mt-0.5 truncate text-sm text-ink-400">{email}</div>
              {profil?.avatar_url && (
                <button
                  onClick={() => entfernen.mutate()}
                  className="mt-1 text-xs text-ink-500 underline-offset-2 hover:text-ink-300 hover:underline"
                >
                  Bild entfernen
                </button>
              )}
            </div>
          </div>

          <input
            ref={bildRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = ""; // sonst löst dieselbe Datei kein zweites Mal aus
              if (!f) return;
              setBildFehler(null);
              try {
                await hochladen.mutateAsync(f);
              } catch (err) {
                setBildFehler((err as Error).message);
              }
            }}
          />

          {bildFehler && (
            <p className="mt-3 rounded-lg border border-danger-bg/40 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
              {bildFehler}
            </p>
          )}

          <div className="mt-3 flex items-center justify-between border-t border-ink-700/70 pt-3 text-sm">
            <span className="text-ink-400">Rolle</span>
            <span className="font-medium text-ink-100">{rolleLabel}</span>
          </div>
        </Card>

        {/* Meine Arbeitszeiten — der häufigste Grund, hier zu landen */}
        <button
          onClick={() => navigate("/crm/zeit")}
          className={cn(
            "flex w-full items-center gap-3 rounded-xl border border-ink-700 bg-ink-800/60 px-4 py-4 text-left",
            "transition-colors duration-fast hover:bg-ink-800",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60"
          )}
        >
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-violet-500/15 text-violet-300">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12.5" r="7.5" />
              <path d="M12 8.5v4l2.5 1.8" />
              <path d="M9 2.5h6" />
            </svg>
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[15px] font-medium text-ink-100">Meine Arbeitszeiten</span>
            <span className="block text-sm text-ink-400">Stempeluhr, Stunden, Urlaub</span>
          </span>
          <span className="shrink-0 text-ink-500">›</span>
        </button>

        {/* Darstellung — stand früher als Symbol oben rechts. Hier gehört es hin:
            zu den anderen Dingen, die nur einen selbst betreffen. */}
        <section>
          <h2 className="mb-2.5 text-xs font-medium uppercase tracking-wider text-ink-400">
            Darstellung
          </h2>
          <Segmented
            value={mode}
            onChange={setTheme}
            options={[
              { value: "system", label: "System" },
              { value: "light", label: "Hell" },
              { value: "dark", label: "Dunkel" },
            ]}
          />
          <p className="mt-2 text-sm text-ink-500">
            „System“ folgt der Einstellung des Handys — hell am Tag, dunkel am Abend.
          </p>
        </section>

        {/* E-Mail ändern */}
        <section>
          <h2 className="mb-2.5 text-xs font-medium uppercase tracking-wider text-ink-400">
            E-Mail-Adresse
          </h2>
          <Field
            label="Neue Adresse"
            hint="Du bekommst eine Bestätigungsmail an die NEUE Adresse. Erst der Klick darin schaltet um — bis dahin gilt die alte."
          >
            <Input
              type="email"
              value={neueMail}
              onChange={(e) => setNeueMail(e.target.value)}
              placeholder={email}
              autoComplete="email"
            />
          </Field>

          {mailHinweis && (
            <p
              className={cn(
                "mt-3 rounded-lg border px-3 py-2 text-sm",
                mailHinweis.ok
                  ? "border-gruen-500/40 bg-gruen-500/10 text-gruen-400"
                  : "border-danger-bg/40 bg-danger-bg/10 text-danger-fg"
              )}
            >
              {mailHinweis.text}
            </p>
          )}

          <Button
            className="mt-3 w-full"
            disabled={!neueMail.trim() || neueMail.trim().toLowerCase() === email.toLowerCase()}
            loading={mailAendern.isPending}
            onClick={async () => {
              setMailHinweis(null);
              try {
                const m = await mailAendern.mutateAsync(neueMail);
                setMailHinweis({
                  ok: true,
                  text: `Bestätigungsmail an ${m} verschickt. Bis zum Klick darin gilt weiterhin ${email}.`,
                });
                setNeueMail("");
              } catch (e) {
                setMailHinweis({ ok: false, text: (e as Error).message });
              }
            }}
          >
            Adresse ändern
          </Button>
        </section>

        {/* Passwort ändern */}
        <section>
          <h2 className="mb-2.5 text-xs font-medium uppercase tracking-wider text-ink-400">
            Passwort ändern
          </h2>

          <div className="space-y-4">
            <Field label="Bisheriges Passwort">
              <Input
                type="password"
                value={alt}
                onChange={(e) => setAlt(e.target.value)}
                autoComplete="current-password"
              />
            </Field>

            <Field label="Neues Passwort" hint="Mindestens 8 Zeichen">
              <Input
                type="password"
                value={neu}
                onChange={(e) => setNeu(e.target.value)}
                autoComplete="new-password"
                className={cn(zuKurz && "border-danger-bg/50")}
              />
            </Field>

            <Field label="Neues Passwort wiederholen">
              <Input
                type="password"
                value={wdh}
                onChange={(e) => setWdh(e.target.value)}
                autoComplete="new-password"
                className={cn(ungleich && "border-danger-bg/50")}
              />
            </Field>

            {ungleich && (
              <p className="text-sm text-danger-fg">Die beiden Eingaben stimmen nicht überein.</p>
            )}

            {fehler && (
              <p className="rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2.5 text-sm text-danger-fg">
                {fehler}
              </p>
            )}

            {erfolg && (
              <p className="rounded-lg border border-gruen-500/30 bg-gruen-500/10 px-3 py-2.5 text-sm text-gruen-400">
                Passwort geändert. Beim nächsten Anmelden gilt das neue.
              </p>
            )}

            <Button
              variant="primary"
              size="lg"
              className="w-full"
              disabled={!kannSpeichern}
              loading={laeuft}
              onClick={aendern}
            >
              Passwort ändern
            </Button>
          </div>
        </section>

        {/* Abmelden */}
        <section className="border-t border-ink-700/70 pt-5">
          <Button
            className="w-full"
            onClick={async () => {
              await signOut();
              navigate("/login", { replace: true });
            }}
          >
            Abmelden
          </Button>
          <p className="mt-2 text-center text-sm text-ink-500">
            Danach kann sich hier jemand anderes anmelden.
          </p>
        </section>
      </div>
    </div>
  );
}
